import { Controller } from "@hotwired/stimulus"

// Animated segmented control: a "pill" slides between tab buttons.
// Active label crossfades color via CSS. Works in LTR and RTL.
//
// Panels are optional. Most tabs here swap a Turbo frame, so they have none.
// When a caller does supply `panel` targets — each carrying the `data-tab-key`
// of the tab it belongs to — the controller shows the matching one and hides
// the rest, which is what a tab whose content is already on the page needs.
export default class extends Controller {
  static targets = ["tab", "pill", "panel"]
  static values = { active: String }

  connect() {
    this.boundReposition = () => this.positionPill(false)
    // Defer initial measurement: on Turbo cache restore the layout isn't
    // flushed when connect() fires, so getBoundingClientRect can return 0
    // for the active tab and the pill collapses to a sliver.
    requestAnimationFrame(() => this.positionPill(false))
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.positionPill(false))
    }
    window.addEventListener("resize", this.boundReposition)
    document.addEventListener("turbo:load", this.boundReposition)
    document.addEventListener("turbo:render", this.boundReposition)

    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.positionPill(false))
      this.resizeObserver.observe(this.element)
    }
  }

  disconnect() {
    window.removeEventListener("resize", this.boundReposition)
    document.removeEventListener("turbo:load", this.boundReposition)
    document.removeEventListener("turbo:render", this.boundReposition)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  }

  select(event) {
    const tab = event.currentTarget
    const key = tab.dataset.tabKey
    if (key === this.activeValue) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    this.activeValue = key
    this.tabTargets.forEach(t => {
      const isActive = t.dataset.tabKey === key
      t.classList.toggle("is-active", isActive)
      t.setAttribute("aria-selected", isActive ? "true" : "false")
    })
    this.showActivePanel()
    this.positionPill(true)
  }

  showActivePanel() {
    if (!this.hasPanelTarget) return

    // A class, not `hidden`: display:none cancels transitions, and these
    // panels cross-dissolve. Keeping both in flow is the point — they share a
    // grid cell, so the row never reflows mid-swap.
    this.panelTargets.forEach(panel => {
      panel.classList.toggle("is-active", panel.dataset.tabKey === this.activeValue)
    })
  }

  positionPill(animated) {
    const active = this.tabTargets.find(t => t.dataset.tabKey === this.activeValue) || this.tabTargets[0]
    if (!active || !this.hasPillTarget) return

    const trackRect = this.element.getBoundingClientRect()
    const tabRect = active.getBoundingClientRect()
    if (tabRect.width === 0 || trackRect.width === 0) {
      // Layout not ready yet — try again next frame instead of writing a
      // bad (collapsed) width onto the pill.
      requestAnimationFrame(() => this.positionPill(animated))
      return
    }

    // The pill is anchored at the inline-start edge (`start-0`), which is the
    // right edge under RTL. Measure the offset from that same edge so the
    // transform moves the pill the correct way in both directions. Same RTL
    // detection the carousels use.
    const isRtl = getComputedStyle(this.element).direction === "rtl"
    const offset = isRtl
      ? -(trackRect.right - tabRect.right)
      : (tabRect.left - trackRect.left)

    const pill = this.pillTarget
    pill.style.transition = animated
      ? "transform 220ms cubic-bezier(0.4, 0, 0.2, 1), width 220ms cubic-bezier(0.4, 0, 0.2, 1)"
      : "none"
    pill.style.width = `${tabRect.width}px`
    pill.style.transform = `translateX(${offset}px)`
  }
}
